import React, { useMemo } from 'react';
import ReusableTabs from '../common/ReusableTabs';
import { ResourceLibraryItemBreadCrumbs } from '../common/BreadCrumbs';
import {
    ResourceLibrarySitePageItem,
    isSitePageApplicationArticleItem,
} from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibrarySitePageItems';
import sectionStyles from './structuredSections/StructuredSections.module.scss';
import { useCommonContent } from '../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';
import { useProductGroups } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ProductGroupsProvider';
import { useResourceTypes } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceTypesProvider';
import { useBrands } from '../../graphql/pimcore/modules/resourceLibrary/contexts/BrandsProvider';
import { useNavigationNodes } from '../../graphql/pimcore/modules/siteNavigation/contexts/NavigationNodesProvider';
import { PrefilteredResourceLibraryItemsProvider } from '../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import PreFilteredResourcesList from '../common/resourceLibrary/PreFilteredResourcesList';
import { useItemHeaderBackground } from '../../graphql/pimcore/modules/resourceLibrary/hooks/useItemHeaderBackground';
import {
    buildKeyFiltersFromItem,
    pickKeyFilters,
} from '../../graphql/pimcore/modules/resourceLibrary/tools/buildKeyFiltersFromItem';
import { processSections } from './structuredSections/sectionProcessing';
import {
    SectionGroup,
    SitePageSectionGroup,
} from './structuredSections/sitePage/SitePageSectionGroup';
import pimcoreClient from '../../graphql/pimcore/apollo/pimcoreClient';
import {
    collectPimcoreAssetIdsFromHtmlBlocks,
    replacePimcoreAssetPlaceholders,
    usePimcoreAssets,
} from '../../utils/pimcoreAssetPlaceholders';

interface SitePageProps {
    item: ResourceLibrarySitePageItem;
}

const SitePage: React.FC<SitePageProps> = ({ item }) => {
    const { contentTree: cmsResourceLibraryContent } = useCommonContent('ResourceLibrary');
    const cmsSitePageContent = cmsResourceLibraryContent.itemClasses.SitePageItem;
    const { productGroupById } = useProductGroups();
    const { brandById } = useBrands();
    const { resourceTypeByKey } = useResourceTypes();
    const { navigationNodeById } = useNavigationNodes();
    const templatesContent = cmsSitePageContent.templates;
    const applicationArticleTemplateContent = cmsSitePageContent.templates.ApplicationArticle;

    const headerBackgroundImage = useItemHeaderBackground({
        item,
        productGroupById,
        fallbackPath: templatesContent[item.template].defaultPageTitleBackgroundImage.imageUrl,
    });

    const processedSections = useMemo(() => processSections(item.sections), [item.sections]);

    const sitePageAssetIds = useMemo(() => {
        const htmlBlocks: string[] = [];

        processedSections.forEach((section) => {
            if (section.sanitizedRichText) {
                htmlBlocks.push(section.sanitizedRichText);
            }
            if (section.richTableBlock?.tableHtml) {
                htmlBlocks.push(section.richTableBlock.tableHtml);
            }
        });

        return collectPimcoreAssetIdsFromHtmlBlocks(htmlBlocks);
    }, [processedSections]);

    const { assetsById: sitePageAssetsById } = usePimcoreAssets(sitePageAssetIds, {
        client: pimcoreClient,
    });

    const processedSectionsWithAssets = useMemo(() => {
        if (sitePageAssetsById.size === 0) {
            return processedSections;
        }

        return processedSections.map((section) => {
            let updatedRichText = section.sanitizedRichText ?? null;
            let updatedRichTableBlock = section.richTableBlock ?? null;
            let hasChanges = false;

            if (section.sanitizedRichText) {
                const next = replacePimcoreAssetPlaceholders(
                    section.sanitizedRichText,
                    sitePageAssetsById
                );
                if (next !== section.sanitizedRichText) {
                    updatedRichText = next;
                    hasChanges = true;
                }
            }

            if (section.richTableBlock) {
                const next = replacePimcoreAssetPlaceholders(
                    section.richTableBlock.tableHtml,
                    sitePageAssetsById
                );
                if (next !== section.richTableBlock.tableHtml) {
                    updatedRichTableBlock = {
                        ...section.richTableBlock,
                        tableHtml: next,
                    };
                    hasChanges = true;
                }
            }

            if (!hasChanges) {
                return section;
            }

            const updatedSection = {
                ...section,
            };

            if (updatedRichText !== null && updatedRichText !== section.sanitizedRichText) {
                updatedSection.sanitizedRichText = updatedRichText;
            }

            if (updatedRichTableBlock && updatedRichTableBlock !== section.richTableBlock) {
                updatedSection.richTableBlock = updatedRichTableBlock;
            }

            return updatedSection;
        });
    }, [processedSections, sitePageAssetsById]);

    const sectionGroups = useMemo<SectionGroup[]>(() => {
        const groups: SectionGroup[] = [];
        let currentGroup: SectionGroup | null = null;

        processedSectionsWithAssets.forEach((section) => {
            if (!currentGroup || !section.mergeWithPrevious) {
                currentGroup = {
                    id: `${section.id}-group`,
                    sections: [],
                    background: section.background,
                    mergeWithPrevious: section.mergeWithPrevious,
                };
                groups.push(currentGroup);
            }

            currentGroup.sections.push(section);
        });

        return groups;
    }, [processedSectionsWithAssets]);

    const showTabs = isSitePageApplicationArticleItem(item);
    const tabsCopy = cmsSitePageContent.common?.tabs ?? applicationArticleTemplateContent.tabs;
    const templateTabLabels: [string, string, string] = [
        tabsCopy.Overview.title.content,
        tabsCopy.Datasheets.title.content,
        tabsCopy.Resources.title.content,
    ];

    const datasheetResourceTypeKey = useMemo(() => {
        if (resourceTypeByKey['Datasheet']) {
            return 'Datasheet';
        }
        const match = Object.entries(resourceTypeByKey).find(
            ([, resourceType]) => resourceType.name.toLowerCase() === 'datasheet'
        );
        return match ? match[0] : null;
    }, [resourceTypeByKey]);

    const resourceTypeKeysExcludingDatasheet = useMemo(() => {
        return Object.keys(resourceTypeByKey).filter((key) => key !== datasheetResourceTypeKey);
    }, [resourceTypeByKey, datasheetResourceTypeKey]);

    const baseKeyFilters = useMemo(
        () =>
            buildKeyFiltersFromItem(item, {
                brandById,
                productGroupById,
                navigationNodeById,
            }),
        [item, brandById, productGroupById, navigationNodeById]
    );

    const datasheetsInitialFilters = useMemo(() => {
        const base = pickKeyFilters(baseKeyFilters, [
            'brandKey',
            'productGroupKeys',
            'navigationScopePaths',
            'exclusiveNavigationScopePaths',
        ]);

        if (datasheetResourceTypeKey) {
            return {
                ...base,
                resourceTypeKeys: [datasheetResourceTypeKey],
            };
        }

        return base;
    }, [baseKeyFilters, datasheetResourceTypeKey]);

    const resourcesInitialFilters = useMemo(() => {
        const base = pickKeyFilters(baseKeyFilters, [
            'brandKey',
            'productGroupKeys',
            'navigationScopePaths',
            'exclusiveNavigationScopePaths',
        ]);

        if (resourceTypeKeysExcludingDatasheet.length > 0) {
            return {
                ...base,
                resourceTypeKeys: resourceTypeKeysExcludingDatasheet,
            };
        }

        return base;
    }, [baseKeyFilters, resourceTypeKeysExcludingDatasheet]);

    const overviewContent = (
        <div className={sectionStyles.root}>
            <div className={sectionStyles.sectionWrapper}>
                {sectionGroups.map((group) => (
                    <SitePageSectionGroup key={group.id} group={group} />
                ))}
            </div>
        </div>
    );

    const datasheetsContent = showTabs ? (
        <PrefilteredResourceLibraryItemsProvider initialFilters={datasheetsInitialFilters}>
            <PreFilteredResourcesList />
        </PrefilteredResourceLibraryItemsProvider>
    ) : null;

    const resourcesContent = showTabs ? (
        <PrefilteredResourceLibraryItemsProvider initialFilters={resourcesInitialFilters}>
            <PreFilteredResourcesList />
        </PrefilteredResourceLibraryItemsProvider>
    ) : null;

    const tabLabels = showTabs ? templateTabLabels : [];
    const tabKeys = showTabs ? ['Overview', 'Datasheets', 'Resources'] : [''];
    const tabContents = showTabs
        ? [overviewContent, datasheetsContent!, resourcesContent!]
        : [overviewContent];

    return (
        <ReusableTabs
            pageName="resourceSitePage"
            bannerImage={headerBackgroundImage}
            bannerTitle={item.name}
            bannerDescription={item.description}
            tabLabels={tabLabels}
            tabKeys={tabKeys}
            tabContents={tabContents}
            breadcrumbs={<ResourceLibraryItemBreadCrumbs item={item} />}
            showTabs={showTabs}
            isHtmlAsset={false}
            backgroundColor="#7A7C81"
            changeUrl={showTabs ? undefined : false}
        />
    );
};

export default SitePage;
